import { useEffect, useState } from "react";
import { KeyRound, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { forgetDevice, initialsOf, setTrusted, trustedAccounts } from "@/lib/trusted";
import { glassCard, glassInput } from "@/components/login/LoginShell";
import { DigitBoxes, RevealButton, SecretInput } from "@/components/login/Secret";
import { RecoverFlow } from "@/components/login/RecoverFlow";

const METHODS = [["password", "Password"], ["pin", "PIN"]];
const scopeOf = (slug) => (slug || "").toLowerCase();

const Account = ({ a, onClick, active }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={`pin-account-${a.masked}`}
    className={cn("flex min-h-[56px] w-full items-center gap-3 rounded-xl bg-white/10 px-3 py-2 text-left", onClick && "hover:bg-white/15", active && "ring-2 ring-teal-400")}>
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-500 text-sm font-bold text-white">{initialsOf(a.name)}</span>
    <span className="min-w-0"><span className="block truncate font-semibold text-white" data-testid="pin-account-name">{a.name}</span><span className="block truncate text-xs text-slate-300" data-testid="pin-account-email">{a.masked}</span></span>
  </button>
);

/** Sign-in card for /login and /login/<slug>: Password, or PIN on a device trusted by an earlier password sign-in (no email). */
export const LoginCard = ({ slug, header, title, subtitle, footer, notice, onSignedIn }) => {
  const { login } = useAuth();
  const scope = scopeOf(slug);
  const [accounts, setAccounts] = useState(() => trustedAccounts(scope));
  const [method, setMethod] = useState(() => (trustedAccounts(scope).length ? "pin" : "password"));
  const [pick, setPick] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(() => { const n = sessionStorage.getItem("login_notice"); sessionStorage.removeItem("login_notice"); return n || ""; });
  const [success, setSuccess] = useState("");
  const [invite, setInvite] = useState(() => new URLSearchParams(window.location.search).get("invite"));
  const [recover, setRecover] = useState(() => (invite ? "password" : null));
  const endInvite = () => { if (invite) { setInvite(null); window.history.replaceState(null, "", window.location.pathname); } };

  useEffect(() => {
    const stored = trustedAccounts(scope);
    if (!stored.length) return;
    api.post("/auth/devices/lookup", { tokens: stored.map((a) => a.token), ...(scope && { company: scope }) }).then((r) => {
      const ok = stored.filter((a) => r.data.accounts.some((x) => x.token === a.token)).map((a) => ({ ...a, ...r.data.accounts.find((x) => x.token === a.token) }));
      setTrusted(scope, ok); setAccounts(ok);
      if (!ok.length) setMethod("password");
    }).catch(() => {});
  }, [scope]);

  const current = accounts.length === 1 ? accounts[0] : accounts.find((a) => a.token === pick) || null;
  const choose = (m) => { setMethod(m); setError(""); setShown(false); setPin(""); };
  const notYou = () => {
    api.post("/auth/devices/forget", { token: current.token }).catch(() => {});
    forgetDevice(scope, current.token);
    const rest = accounts.filter((a) => a.token !== current.token);
    setAccounts(rest); setPick(null); setEmail(current.email); choose("password");
  };
  const signIn = async (secret) => {
    setError(""); setSuccess(""); setBusy(true);
    try {
      onSignedIn(await login(method === "pin" ? { device_token: current.token, pin: secret } : { email, password }, slug));
    } catch (err) {
      const s = err?.response?.status;
      if (method === "pin" && s === 401 && /no longer remembered/.test(apiError(err))) { forgetDevice(scope, current.token); setAccounts((l) => l.filter((a) => a.token !== current.token)); setPick(null); }
      setError(method === "password" && s === 401 ? "Invalid email or password" : apiError(err));
      if (method === "pin") setPin("");
    } finally { setBusy(false); }
  };

  if (recover) return (
    <div className={glassCard} data-testid="recover-card">
      {header}
      <RecoverFlow kind={recover} slug={slug} initialEmail={(recover === "pin" && current?.email) || email} inviteToken={invite} onCancel={() => { endInvite(); setRecover(null); }}
        onDone={(msg) => { endInvite(); setRecover(null); setSuccess(invite ? "Password set. Sign in with your email and new password." : msg); setError(""); setPassword(""); setPin(""); }} />
    </div>
  );
  const pinTab = !accounts.length ? (
    <div className="space-y-3 rounded-xl bg-white/5 p-4 text-sm text-slate-200" data-testid="pin-untrusted">
      <p className="flex gap-2"><KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-teal-300" /> Sign in once with your email and password on this device to use your PIN.</p>
      <Button type="button" onClick={() => choose("password")} className="h-11 w-full bg-white/15 text-white hover:bg-white/25" data-testid="pin-use-password">Use password</Button>
    </div>
  ) : !current ? (
    <div className="space-y-2" data-testid="pin-account-picker">
      <Label className="text-slate-200">Choose your account</Label>
      {accounts.map((a) => <Account key={a.token} a={a} onClick={() => { setPick(a.token); setPin(""); setError(""); }} />)}
    </div>
  ) : (
    <div className="space-y-4">
      <Account a={current} onClick={accounts.length > 1 ? () => setPick(null) : null} active />
      <div className="space-y-1.5"><Label className="text-slate-200">4-digit PIN</Label>
        <div className="flex items-center gap-2">
          <DigitBoxes value={pin} onChange={setPin} onComplete={(v) => !busy && signIn(v)} shown={shown} label="PIN" testid="login-pin" autoFocus dark />
          <RevealButton shown={shown} onToggle={() => setShown(!shown)} label="PIN" className="text-slate-300 hover:text-white" testid="login-pin-toggle" />
        </div></div>
      <button type="button" onClick={notYou} className="min-h-[44px] text-sm text-slate-300 underline hover:text-white" data-testid="pin-not-you">Not you? Use another account</button>
    </div>
  );

  return (
    <form onSubmit={(e) => { e.preventDefault(); signIn(method === "pin" ? pin : password); }} className={glassCard} data-testid="login-form" noValidate>
      {header}
      {title}
      {subtitle && <p className="mt-1 text-sm text-slate-300">{subtitle}</p>}
      {notice}
      <div className="mt-5 grid grid-cols-2 rounded-xl bg-white/10 p-1" role="tablist" aria-label="Sign-in method" data-testid="login-method-toggle">
        {METHODS.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={method === k} onClick={() => choose(k)} data-testid={`login-method-${k}`}
            className={cn("min-h-[44px] rounded-lg text-sm font-semibold", method === k ? "bg-white text-slate-900 shadow" : "text-slate-200 hover:text-white")}>{l}</button>
        ))}
      </div>
      <div className="mt-5 space-y-4">
        {method === "password" ? (<>
          <div className="space-y-1.5"><Label htmlFor="email" className="text-slate-200">Email</Label><Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className={glassInput} /></div>
          <div className="space-y-1.5"><Label htmlFor="password" className="text-slate-200">Password</Label>
            <SecretInput id="password" autoComplete="current-password" value={password} onChange={setPassword} shown={shown} onToggle={() => setShown(!shown)} className={glassInput} testid="login-password-input" /></div>
        </>) : pinTab}
        {success && <p className="rounded-md border border-teal-300/40 bg-teal-500/15 px-3 py-2 text-sm text-teal-50" data-testid="login-success">{success}</p>}
        {error && <p className="rounded-md border border-red-300/40 bg-red-500/15 px-3 py-2 text-sm text-red-100" data-testid="login-error">{error}</p>}
        {(method === "password" || current) && (
          <Button type="submit" disabled={busy || (method === "pin" ? pin.length !== 4 : !email || !password)} className="h-12 w-full bg-teal-500 text-base font-semibold text-white hover:bg-teal-400" data-testid="login-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
        )}
        <div className="flex justify-between gap-2 text-sm">
          <button type="button" onClick={() => setRecover("password")} className="min-h-[44px] text-teal-300 hover:text-teal-200 hover:underline" data-testid="forgot-password-link">Forgot password?</button>
          <button type="button" onClick={() => setRecover("pin")} className="min-h-[44px] text-teal-300 hover:text-teal-200 hover:underline" data-testid="forgot-pin-link">Forgot PIN?</button>
        </div>
      </div>
      <p className="mt-4 flex items-center gap-2 text-xs text-slate-300"><Lock className="h-3.5 w-3.5" /> {footer}</p>
    </form>
  );
};
